import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useAuthStore } from '@/features/auth/authStore';
import { takePendingShopifyLink } from '@/features/shopify/pendingClaim';
import { apiClient } from '@/lib/apiClient';
import type {
  LoginRequest,
  RegisterRequest,
  GoogleSignInRequest,
  AuthResponse,
  User,
  ApiResponse,
} from '@/types/api';

export const useAuth = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setAuth, clearAuth, token, user } = useAuthStore();

  // Every sign-in path first returns to a Shopify store link the visitor started before signing in.

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: (data: LoginRequest) =>
      apiClient.post<ApiResponse<AuthResponse>>('/api/auth/login', data),
    onSuccess: (response) => {
      if (response?.data?.data) {
        const authData = response.data.data;
        setAuth(authData.token, authData.user);
        queryClient.invalidateQueries({ queryKey: ['user'] });
        toast.success('Welcome back!');
        navigate(takePendingShopifyLink() ?? '/dashboard');
      }
    },
    onError: (error: any) => {
      console.error('Login failed:', error);
    }
  });

  // Register mutation
  const registerMutation = useMutation({
    mutationFn: (data: RegisterRequest) =>
      apiClient.post<ApiResponse<AuthResponse>>('/api/auth/register', data),
    onSuccess: (response) => {
      if (response?.data?.data) {
        const authData = response.data.data;
        setAuth(authData.token, authData.user);
        queryClient.invalidateQueries({ queryKey: ['user'] });
        toast.success('Account created successfully!');
        navigate(takePendingShopifyLink() ?? '/onboarding');
      }
    },
    onError: (error: any) => {
      console.error('Registration failed:', error);
    }
  });

  // Google sign-in. One endpoint signs up a new account or signs in an existing one;
  // `isNewUser` says which, so a first visit goes through onboarding like a registration.
  const googleMutation = useMutation({
    mutationFn: (data: GoogleSignInRequest) =>
      apiClient.post<ApiResponse<AuthResponse>>('/api/auth/google', data),
    onSuccess: (response) => {
      const authData = response?.data?.data;
      if (!authData) return;
      setAuth(authData.token, authData.user);
      queryClient.invalidateQueries({ queryKey: ['user'] });
      toast.success(authData.isNewUser ? 'Account created successfully!' : 'Welcome back!');
      navigate(takePendingShopifyLink() ?? (authData.isNewUser ? '/onboarding' : '/dashboard'));
    },
    onError: (error: any) => {
      console.error('Google sign-in failed:', error);
    }
  });

  // User profile query
  const userQuery = useQuery({
    queryKey: ['user'],
    queryFn: () => apiClient.get<ApiResponse<User>>('/api/auth/me').then(res => res.data.data),
    enabled: !!token,
    staleTime: 30 * 60 * 1000, // 30 minutes
    retry: false,
  });

  // Logout function
  const logout = () => {
    clearAuth();
    queryClient.clear();
    toast.success('Logged out successfully');
    navigate('/login');
  };

  return {
    // Functions
    login: loginMutation.mutate,
    register: registerMutation.mutate,
    googleSignIn: googleMutation.mutate,
    logout,

    // State
    user: userQuery.data || user,
    isLoading:
      loginMutation.isPending ||
      registerMutation.isPending ||
      googleMutation.isPending ||
      userQuery.isLoading,
    isAuthenticated: !!token && !!user,

    // Error states
    loginError: loginMutation.error,
    registerError: registerMutation.error,
  };
};
